interface ChoiceOption<T> { label: string; value: T; primary?: boolean; danger?: boolean; subtext?: string; }

export function showAlert(message: string): Promise<void> {
  return showDialog<void>({
    title: '',
    message,
    options: [{ label: 'OK', value: undefined as void, primary: true }],
  });
}

export function showConfirm(title: string, message: string, confirmLabel = 'Confirm'): Promise<boolean> {
  return showDialog<boolean>({
    title, message,
    options: [
      { label: 'Cancel', value: false },
      { label: confirmLabel, value: true, primary: true },
    ],
  });
}

export function showChoice<T>(title: string, message: string, options: ChoiceOption<T>[]): Promise<T | null> {
  return showDialog<T | null>({
    title, message,
    options: [...options, { label: 'Cancel', value: null as unknown as T }],
  });
}

interface DialogSpec<T> {
  title: string;
  message: string;
  options: ChoiceOption<T>[];
}

function ensureDialogStyles(): void {
  if ((document as any).__dialogStylesInjected) return;
  const style = document.createElement('style');
  style.textContent = `
    .dialog-btn-label { display: block; }
    .dialog-btn-subtext { display: block; font-size: 0.7em; opacity: 0.75; margin-top: 2px; font-weight: normal; }
  `;
  document.head.appendChild(style);
  (document as any).__dialogStylesInjected = true;
}

function showDialog<T>(spec: DialogSpec<T>): Promise<T> {
  return new Promise<T>((resolve) => {
    ensureDialogStyles();
    const backdrop = document.createElement('div');
    backdrop.className = 'editor-dialog-backdrop';
    const modal = document.createElement('div');
    modal.className = 'editor-dialog';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    if (spec.title) modal.setAttribute('aria-labelledby', 'dialog-title');
    const titleEl = spec.title ? `<h3 id="dialog-title">${escape(spec.title)}</h3>` : '';
    const btnsHtml = spec.options.map((o, i) => {
      const labelHtml = `<span class="dialog-btn-label">${escape(o.label)}</span>`;
      const subtextHtml = o.subtext ? `<span class="dialog-btn-subtext">${escape(o.subtext)}</span>` : '';
      const content = labelHtml + subtextHtml;
      return `<button data-idx="${i}" class="${o.primary ? 'primary' : ''} ${o.danger ? 'danger' : ''}">${content}</button>`;
    }).join('');
    modal.innerHTML = `${titleEl}<p>${escape(spec.message)}</p><div class="dialog-buttons">${btnsHtml}</div>`;
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);

    const primary = modal.querySelector<HTMLButtonElement>('button.primary') ?? modal.querySelector<HTMLButtonElement>('button');
    primary?.focus();

    const release = trapFocus(modal);

    modal.querySelectorAll<HTMLButtonElement>('button').forEach((b) => {
      b.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
        const idx = parseInt(b.dataset.idx!, 10);
        release();
        backdrop.remove();
        resolve(spec.options[idx].value);
      });
    });
  });
}

/** Constrain Tab key focus to elements within `container`. Returns cleanup fn. */
function trapFocus(container: HTMLElement): () => void {
  const handler = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const focusable = container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  container.addEventListener('keydown', handler);
  return () => container.removeEventListener('keydown', handler);
}

function escape(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]!));
}

export interface InputSpec {
  label: string;
  initial?: string;
  type?: 'text' | 'color';
  placeholder?: string;
}

export function showInput(title: string, spec: InputSpec): Promise<string | null> {
  return showInputsDialog(title, [spec]).then((vals) => vals ? vals[0] : null);
}

export function showTwoInputs(
  title: string, a: InputSpec, b: InputSpec,
): Promise<[string, string] | null> {
  return showInputsDialog(title, [a, b]).then((vals) =>
    vals ? [vals[0], vals[1]] : null,
  );
}

function showInputsDialog(title: string, specs: InputSpec[]): Promise<string[] | null> {
  return new Promise<string[] | null>((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'editor-dialog-backdrop';
    const modal = document.createElement('div');
    modal.className = 'editor-dialog';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'dialog-title');

    const fields = specs.map((s, i) => {
      const inputType = s.type === 'color' ? 'color' : 'text';
      return `
        <div class="field-row">
          <label for="dlg-input-${i}">${escape(s.label)}</label>
          <input id="dlg-input-${i}" type="${inputType}" value="${escape(s.initial ?? '')}"
                 placeholder="${escape(s.placeholder ?? '')}" />
        </div>
      `;
    }).join('');

    modal.innerHTML = `
      <h3 id="dialog-title">${escape(title)}</h3>
      ${fields}
      <div class="dialog-buttons">
        <button data-act="cancel">Cancel</button>
        <button class="primary" data-act="ok">OK</button>
      </div>
    `;
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);

    const inputs = Array.from(modal.querySelectorAll<HTMLInputElement>('input'));
    inputs[0]?.focus();

    const release = trapFocus(modal);

    const commit = () => {
      const vals = inputs.map((el) => el.value);
      release();
      backdrop.remove();
      resolve(vals);
    };
    const cancel = () => {
      release();
      backdrop.remove();
      resolve(null);
    };

    modal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    });

    modal.querySelector<HTMLButtonElement>('[data-act="ok"]')!.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      commit();
    });
    modal.querySelector<HTMLButtonElement>('[data-act="cancel"]')!.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      cancel();
    });
    inputs.forEach((el) => {
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      });
    });
  });
}
